import Bugsnag from '@bugsnag/js';
import data from '@emoji-mart/data';
import EmojiPicker from '@emoji-mart/react';
import { nanoid } from 'nanoid';
import { useMap } from 'react-map-gl/mapbox';
import type { StampObject as MapStampObject } from '~/common/types';
import { useCivilDrawStore } from '../../../civilDrawStoreContext';
import { customEmojis, customSectionMap, excludedEmojiIds } from './common';

type Coords = [number, number][];

const defaultCategories = [
  'people',
  'nature',
  // NOTE: We do currently have an food & drink emojis selected,
  // hence why its commented out so it doesn't have a blank category.
  // 'foods',
  'activity',
  'places',
  'objects',
  'symbols',
  'flags',
];

interface EmojiPickerProps {
  closePopover: () => void;
}

function StampPicker({ closePopover }: EmojiPickerProps) {
  const store = useCivilDrawStore();
  const { current: map } = useMap();

  return (
    <div className="mb-2">
      <EmojiPicker
        data={data}
        custom={customEmojis}
        exceptEmojis={excludedEmojiIds}
        theme="light"
        navPosition="none"
        categories={[...customEmojis.map((section) => section.id), ...defaultCategories]}
        onEmojiSelect={async (e: any) => {
          try {
            if (!map) {
              throw new Error(`No map found with useMap()`);
            }

            const c = map.project(map.getCenter());
            let w = 50;
            let h = 50;

            // if its a custom item, see if a width/height exist in the custom array.
            if (e.src) {
              const sectionId = e.keywords[0];

              if (sectionId) {
                const customItems = customSectionMap[sectionId];

                if (customItems) {
                  const item = customItems.find(({ label }) => label === e.id);
                  if (item) {
                    if (item.width !== undefined) w = item.width;
                    if (item.height !== undefined) h = item.height;
                  }
                }
              }
            }

            const screenCoords: Coords = [
              [c.x - w / 2, c.y - h / 2],
              [c.x + w / 2, c.y - h / 2],
              [c.x + w / 2, c.y + h / 2],
              [c.x - w / 2, c.y + h / 2],
            ];

            const mapCoords = screenCoords.map((c) => map.unproject(c).toArray()) as Coords;

            const stamp: Omit<MapStampObject, 'createdBy' | 'updatedBy'> = {
              id: nanoid(),
              type: 'stamp',
              title: e.native || e.name || e.id || 'Stamp',
              description: '',
              sortOrder: store.generateSortOrderKey(),
              geometry: {
                type: 'Polygon',
                coordinates: [[...mapCoords, mapCoords[0]]],
              },
              properties: {
                path: '',
                fillOpacity: 1,
                subtype: 'emoji',
                emoji: e.native || e.shortCodes || '',
              },
            };

            if (e.src) {
              delete stamp.properties.subtype;
              delete stamp.properties.emoji;
              stamp.properties.path = e.src.split('stamps/')[1];
            }

            const o = await store.newObject(stamp);

            if (o) {
              store.setCurrentObjectId(o.id);
            } else {
              throw new Error(`Stamp creation failed`);
            }

            closePopover();
          } catch (err) {
            Bugsnag.notify(new Error(`CivilDraw Error creating stamp`, { cause: err }));
          }
        }}
      />
    </div>
  );
}

export default StampPicker;
